import { useEffect, useMemo, useState } from "react";

import { useParams } from "react-router-dom";

import { Layout } from "providers/Layout";
import { Show, InputSize, Input } from "shared/components";
import moment from "moment";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { Tabs } from "shared/components/Tabs/index";

import { BillingDetailSummary } from "./components/BillingDetailSummary";
import { BillingDetailTable } from "./components/BillingDetailTable";
import { BillingDetailCard } from "./components/BillingDetailCard";
import { BillingDetailHeader } from "./components/BillingDetailHeader";

import { useBillingDetailController } from "./controller";
import { getCSVLineItems, normalizeData } from "./utils";
import { TransactionType } from "types/billing";
import { Loader } from "shared/components/Loader";

export const StatementsIdView = (props: {
  statementId?: number;
  onPrint?: () => void;
  printRequested?: boolean;
}) => {
  const { statementId: paramStatementId } = useParams();
  const statementId = String(props.statementId || paramStatementId);

  const {
    statementDetails,
    isLoadingDetails,
    statementActivities,
    isFetchingActivities,
    isLoadingActivities,
    setPage,
    searchQuery,
    setSearchQuery
  } = useBillingDetailController(statementId);

  const [activeTab, setActiveTab] = useState("Summary");

  useEffect(() => {
    const listener = () => {
      props.onPrint?.();
    };
    window.addEventListener("afterprint", listener);
    return () => {
      window.removeEventListener("afterprint", listener);
    };
  }, []);

  // This was is needed. It doesn't work without it. Some weird stuff with useEffect
  const isPrinting =
    statementId &&
    !isLoadingDetails &&
    !isLoadingActivities &&
    props.printRequested;

  useEffect(() => {
    if (isPrinting) window.print();
  }, [isPrinting]);

  const normalizedData = useMemo(() => {
    if (statementDetails?.summary)
      return normalizeData(statementDetails?.summary);

    return;
  }, [statementDetails?.summary]);

  if (isLoadingDetails || isLoadingActivities)
    return (
      <Layout
        header={
          <div className="px-6">
            <div className="no-print">
              <BreadCrumbs
                crumbs={[
                  { title: "Settings", href: "/settings" },
                  { title: "Billing", href: "/settings/statements" },
                  {
                    title:
                      statementDetails?.period.start &&
                      moment(statementDetails.period.start).isValid()
                        ? moment
                            .utc(statementDetails.period.start)
                            .format("MMMM YYYY")
                        : "--",
                    href: `/settings/statements/${statementId}`
                  }
                ]}
              />
            </div>
          </div>
        }
      >
        <div className="h-full w-full flex items-center justify-center">
          <Loader />
        </div>
      </Layout>
    );

  return (
    <Layout
      headerClassName="p-0"
      header={
        <div className="flex flex-col divide-y divide-alpha-40 border-b border-alpha-100 print:border-none">
          <div className="no-print p-[22px]">
            <BreadCrumbs
              crumbs={[
                { title: "Settings", href: "/settings" },
                { title: "Billing", href: "/settings/statements" },
                {
                  title:
                    statementDetails?.period.start &&
                    moment(statementDetails.period.start).isValid()
                      ? moment
                          .utc(statementDetails.period.start)
                          .format("MMMM YYYY")
                      : "--",
                  href: `/settings/statements/${statementId}`
                }
              ]}
            />
          </div>
          {statementDetails && (
            <>
              <div className="px-6">
                <BillingDetailHeader
                  statementId={statementId ? String(statementId) : ""}
                  searchQuery={searchQuery}
                  statementDetails={statementDetails}
                  statementActivities={statementActivities}
                  getCSVLineItems={getCSVLineItems}
                  activeTab={activeTab}
                />
              </div>
              <div className="px-6">
                <BillingDetailCard
                  normalizedData={normalizedData}
                  statementDetails={statementDetails}
                />
              </div>
            </>
          )}
        </div>
      }
    >
      <>
        <div className="no-print">
          <Tabs
            selected={activeTab}
            setActiveTab={setActiveTab}
            tabs={["Summary", "Details"]}
            variant="underline"
          />
        </div>
        <div className="no-print max-w-[271px] pt-4">
          <Input
            id="search"
            autoComplete="off"
            placeholder="Search"
            size={InputSize.SM}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="pt-2 print:!px-12">
          <Show if={activeTab === "Summary"}>
            <BillingDetailSummary summaryData={normalizedData} />
          </Show>
          <Show if={activeTab === "Details"}>
            <BillingDetailTable
              statementActivities={statementActivities}
              isLoading={isFetchingActivities}
              setPage={setPage}
              type={TransactionType.Invoice}
            />
          </Show>
        </div>
      </>
    </Layout>
  );
};
